import React, { FC, useMemo } from 'react';
import styled from 'styled-components';
import { BasicContainer, DetailTextLarge, FlexRow } from '@minecraft.atoms';

type ProgressBarFillProps = {
  $percentage: number;
};

const ProgressBarFill = styled.div<ProgressBarFillProps>`
  transition: width 0.3s ease-in-out;
  width: ${(props) => props.$percentage}%;
  height: 100%;
  position: absolute;
  background-color: ${({ theme }) => theme.designSystem.bgColors.progressBarPrimary};
  border-radius: 0.5rem;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    right: -100%;
    bottom: 0;
    background-color: ${({ theme }) => theme.designSystem.bgColors.progressBarShine};
    opacity: 0.15;
    animation: slide 1.5s linear infinite;
  }

  @keyframes slide {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(100%);
    }
  }
`;

export type ProgressBarProps = {
  percentage?: number;
  label?: string;
  qaId?: string;
  testId?: string;
};

export const ProgressBar: FC<ProgressBarProps> = React.memo(
  ({ percentage = 0, label, qaId, testId = 'progress-bar' }) => {
    const progressPercentage = useMemo(
      () => (typeof percentage === 'number' && !Number.isNaN(percentage) ? percentage : 0),
      [percentage]
    );

    const clampedPercentage = useMemo(() => Math.min(Math.max(progressPercentage, 0), 100), [progressPercentage]);

    return (
      <FlexRow
        gap="2"
        wrap="nowrap"
        w="full"
        data-testid={testId}
        data-qa-id={qaId}
        maxW="full"
        className="cn_atom_px-2"
      >
        {label && (
          <DetailTextLarge color="primary" fontWeight="semibold" className="cn_no-wrap-text">
            {label}
          </DetailTextLarge>
        )}
        <BasicContainer
          className="cn_relative"
          bgColor="progressBarEmpty"
          minWidth={10}
          minHeight={1}
          borderRadius="lg"
          w="full"
          aria-valuenow={clampedPercentage}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={label || 'Progress'}
          role="progressbar"
        >
          <ProgressBarFill $percentage={clampedPercentage} role="status" />
        </BasicContainer>
      </FlexRow>
    );
  }
);
